import React, { useState, useEffect, useContext, useMemo } from "react";

import AppModal from "../AppModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";

function ContactStatusBadge({ jobStatus }) {
	if (jobStatus === true || jobStatus === 1) {
		return (
			<span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-success/15 text-success">
				{i18n.t("bulkMessaging.infoModal.sent")}
			</span>
		);
	}
	if (jobStatus === false || jobStatus === 0) {
		return (
			<span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-danger/15 text-danger">
				{i18n.t("bulkMessaging.infoModal.notSent")}
			</span>
		);
	}
	return (
		<span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300">
			{i18n.t("bulkMessaging.infoModal.pending")}
		</span>
	);
}

function SummaryStat({ label, value, variant }) {
	const variantClass =
		variant === "success"
			? "border-success/30 bg-success/10 text-success"
			: variant === "danger"
				? "border-danger/30 bg-danger/10 text-danger"
				: "border-default-200 bg-default-100 text-default-700 dark:border-default-600 dark:bg-default-800 dark:text-default-200";

	return (
		<div
			className={`flex min-w-[7rem] flex-col rounded-md border px-3 py-2 ${variantClass}`}
		>
			<span className="text-xs font-medium opacity-80">{label}</span>
			<span className="text-lg font-semibold leading-tight">{value ?? 0}</span>
		</div>
	);
}

function filterContactsByTab(contacts, tab) {
	switch (tab) {
		case "success":
			return contacts.filter((c) => c.jobStatus === true || c.jobStatus === 1);
		case "failed":
			return contacts.filter((c) => c.jobStatus === false || c.jobStatus === 0);
		case "pending":
			return contacts.filter((c) => c.jobStatus === null || c.jobStatus === undefined);
		default:
			return contacts;
	}
}

function BulkInfoModal({ open, onClose, bulkMessageId }) {
	const { user } = useContext(AuthContext);
	const [loadingInfos, setLoadingInfos] = useState(false);
	const [bulkInfos, setBulkInfos] = useState({});
	const [activeTab, setActiveTab] = useState("all");

	const handleClose = () => {
		onClose();
	};

	useEffect(() => {
		if (!open || !bulkMessageId || user?.companyId == null) return undefined;

		(async () => {
			setLoadingInfos(true);
			try {
				const { data } = await apiV2.get(`/bulk-messaging-infos/${bulkMessageId}`, {
					params: { companyId: user.companyId },
				});
				setBulkInfos(data);
			} catch (error) {
				toastError(error);
			} finally {
				setLoadingInfos(false);
			}
		})();
	}, [bulkMessageId, user?.companyId, open]);

	useEffect(() => {
		if (open) {
			setActiveTab("all");
		}
	}, [open, bulkMessageId]);

	const visibleContacts = useMemo(() => {
		const list = Array.isArray(bulkInfos?.contacts) ? bulkInfos.contacts : [];
		return filterContactsByTab(list, activeTab);
	}, [bulkInfos.contacts, activeTab]);

	const tabs = [
		{ id: "all", label: i18n.t("bulkMessaging.infoModal.tabAll") },
		{ id: "success", label: i18n.t("bulkMessaging.infoModal.tabSuccess") },
		{ id: "failed", label: i18n.t("bulkMessaging.infoModal.tabFailed") },
		{ id: "pending", label: i18n.t("bulkMessaging.infoModal.tabPending") },
	];

	const showErrorColumn = activeTab === "failed" || activeTab === "all";

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title={i18n.t("bulkMessaging.infoModal.title")}
			maxWidth={920}
			footer={
				<button type="button" className="btn btn-primary" onClick={handleClose}>
					{i18n.t("bulkMessaging.infoModal.close")}
				</button>
			}
		>
			{loadingInfos ? (
				<div className="app-modal-loading">
					<div className="app-modal-spinner" />
				</div>
			) : (
				<>
					<div className="mb-4 flex flex-wrap gap-3">
						<SummaryStat
							label={i18n.t("bulkMessaging.infoModal.summarySuccess")}
							value={bulkInfos?.success}
							variant="success"
						/>
						<SummaryStat
							label={i18n.t("bulkMessaging.infoModal.summaryFailed")}
							value={bulkInfos?.failed}
							variant="danger"
						/>
						<SummaryStat
							label={i18n.t("bulkMessaging.infoModal.summaryPending")}
							value={bulkInfos?.pending}
							variant="neutral"
						/>
					</div>

					<div className="mb-3 flex flex-wrap gap-2">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								type="button"
								className={`btn btn-sm ${activeTab === tab.id ? "btn-primary" : "btn-outline-primary"}`}
								onClick={() => setActiveTab(tab.id)}
							>
								{tab.label}
							</button>
						))}
					</div>

					{visibleContacts.length === 0 ? (
						<p className="m-0 py-8 text-center text-sm text-default-500 dark:text-default-400">
							{i18n.t("bulkMessaging.infoModal.empty")}
						</p>
					) : (
						<div className="overflow-x-auto rounded-md border border-default-200 dark:border-default-700">
							<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
								<thead className="bg-default-150 dark:bg-default-800/60">
									<tr className="text-sm font-normal text-default-700 dark:text-default-300">
										<th className="px-3.5 py-3 text-start">
											{i18n.t("bulkMessaging.infoModal.name")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("bulkMessaging.infoModal.contact")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("bulkMessaging.infoModal.status")}
										</th>
										{showErrorColumn && (
											<th className="px-3.5 py-3 text-start">
												{i18n.t("bulkMessaging.infoModal.errorReason")}
											</th>
										)}
										<th className="px-3.5 py-3 text-center">
											{i18n.t("bulkMessaging.infoModal.attempts")}
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-default-200 dark:divide-default-700">
									{visibleContacts.map((contact) => {
										const { name, number, attempts, jobStatus, id, lastError } = contact;
										return (
											<tr
												key={id || `${number}-${name}`}
												className="text-sm text-default-800 dark:text-default-200"
											>
												<td className="whitespace-nowrap px-3.5 py-2.5">
													{name || "—"}
												</td>
												<td className="whitespace-nowrap px-3.5 py-2.5">
													{number || "—"}
												</td>
												<td className="px-3.5 py-2.5">
													<ContactStatusBadge jobStatus={jobStatus} />
												</td>
												{showErrorColumn && (
													<td className="max-w-xs px-3.5 py-2.5 text-xs text-default-600 dark:text-default-400">
														{lastError || "—"}
													</td>
												)}
												<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
													{attempts ?? 0}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</>
			)}
		</AppModal>
	);
}

export default BulkInfoModal;
